import { useMemo } from 'react';
import { UserContainer } from '../providers';

interface DemoModeResult {
  isDemoMode: boolean;
  isReadOnly: boolean;
  userRole: string | null;
}

/**
 * Hook to check if the current business is in demo mode
 * and whether the user has read-only access
 */
export const useDemoMode = (): DemoModeResult => {
  const { state } = UserContainer.useContainer();
  const { business, permissions } = state ?? {};

  const result = useMemo(() => {
    // Check if business is demo account
    const isDemoMode = business?.is_demo_account === true;

    // Check user's role - member and viewer are read-only roles
    const userRole = permissions?.role ?? null;
    const isViewerRole = userRole === 'member' || userRole === 'viewer';

    // User is read-only if they're in demo mode OR have a viewer/member role
    const isReadOnly = isDemoMode || isViewerRole;

    return {
      isDemoMode,
      isReadOnly,
      userRole,
    };
  }, [business?.is_demo_account, permissions?.role]);

  return result;
};

interface ReadOnlyModeResult extends DemoModeResult {
  enforceReadOnly: <T>(
    callback: () => T,
    readOnlyCallback?: () => T
  ) => T | null;
  getReadOnlyProps: (
    additionalProps?: Record<string, any>
  ) => Record<string, any>;
}

/**
 * Hook to get read-only enforcement utilities
 */
export const useReadOnlyMode = (): ReadOnlyModeResult => {
  const { isReadOnly, isDemoMode, userRole } = useDemoMode();

  /**
   * Execute a function only if user has write access
   */
  const enforceReadOnly = <T>(
    callback: () => T,
    readOnlyCallback?: () => T
  ): T | null => {
    if (isReadOnly) {
      if (readOnlyCallback) {
        return readOnlyCallback();
      }
      // Action blocked: Read-only mode
      return null;
    }
    return callback();
  };

  /**
   * Get props to disable form elements in read-only mode
   */
  const getReadOnlyProps = (
    additionalProps: Record<string, any> = {}
  ): Record<string, any> => {
    let titleText = '';
    if (isReadOnly) {
      titleText = isDemoMode
        ? 'This action is not available in demo mode'
        : 'You do not have permission to perform this action';
    }

    return {
      disabled: isReadOnly,
      readOnly: isReadOnly,
      title: titleText,
      ...additionalProps,
    };
  };

  return {
    isReadOnly,
    isDemoMode,
    userRole,
    enforceReadOnly,
    getReadOnlyProps,
  };
};

export default useDemoMode;
